/**
 * WorkspaceCard — shared visual card for problem workspaces.
 *
 * Full-bleed image with translucent scrim overlay in 3 zones:
 *   1. Peek   (top)    — phase badge + title           — always visible
 *   2. Scroll (middle) — description text               — revealed on hover, scrollable
 *   3. Footer (bottom) — segmented progress + meta      — always visible, sticky
 *
 * Location badge (top-right) shows flag + community name or scope label.
 * Red left accent for problem-pillar branding.
 * On hover: scrim expands upward to reveal full description with scroll.
 *
 * Used by:
 *   - MyWorkspacesTab (profile page)
 *   - WorkspaceList (community Alignment page)
 *
 * Admin features (kebab menu, crop, edit, delete) are overlaid by the parent.
 */

import React from "react";
import { useAppTranslation } from "../../i18n/useAppTranslation";
import { WORKSPACE_PHASES, type WorkspaceResponse } from "../../api/workspaces";
import { mediaSrc } from "../../api/mediaUrl";
import styles from "./WorkspaceCard.module.css";

/* ── Phase badge color mapping ── */
const PHASE_COLORS: Record<string, string> = {
  signal_detection: "var(--status-info, #3b82f6)",
  detection_validation: "var(--status-info, #3b82f6)",
  analysis: "var(--status-warning, #f59e0b)",
  solution_design: "var(--status-warning, #f59e0b)",
  resourcing: "var(--pillar-problems, #ef4444)",
  implementation: "var(--pillar-solutions, #22c55e)",
  sustainment: "var(--pillar-solutions, #22c55e)",
};

export interface LocationBadge {
  flag: string;
  label: string;
}

interface Props {
  workspace: WorkspaceResponse;
  /** Location badge — flag + label (community name, "Self", "My Household"). */
  locationBadge?: LocationBadge;
  /** Click handler — navigate to workspace detail. */
  onClick: () => void;
  /** Optional children rendered over the image (e.g. kebab menu, upload spinner). */
  children?: React.ReactNode;
}

/* ── Phase label ── */
function phaseLabel(phase: string, t: (key: string) => string): string {
  return t(`workspaces.phases.${phase}`) || phase.replace(/_/g, " ");
}

/* ── Relative time ── */
function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

const WorkspaceCard: React.FC<Props> = ({
  workspace: ws,
  locationBadge,
  onClick,
  children,
}) => {
  const { t } = useAppTranslation("userProfile");

  const phaseColor = PHASE_COLORS[ws.phase] || "var(--text-muted)";
  const phaseIdx = WORKSPACE_PHASES.indexOf(
    ws.phase as typeof WORKSPACE_PHASES[number]
  );

  const heroKey = ws.detailHeroImageUrl ?? ws.heroImageUrl;
  const heroSrc = mediaSrc(heroKey);

  return (
    <button
      type="button"
      className={styles.card}
      onClick={onClick}
    >
      {/* Full-bleed hero image */}
      <div className={styles.cardImageWrap}>
        {heroSrc ? (
          <img
            className={styles.cardImage}
            src={heroSrc}
            alt={ws.title || ws.presentingConcern}
            loading="lazy"
          />
        ) : (
          <div className={styles.cardImageFallback}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
              <rect x="9" y="3" width="6" height="4" rx="1" />
              <path d="M12 11v4M10 13h4" />
            </svg>
          </div>
        )}
      </div>

      {/* Location badge (top-right) */}
      {locationBadge && (
        <span className={styles.locationBadge}>
          {locationBadge.flag && (
            <span className={styles.locationFlag}>{locationBadge.flag}</span>
          )}
          {locationBadge.label}
        </span>
      )}

      {/* Admin overlays (kebab, spinner, etc.) from parent */}
      {children}

      {/* Translucent scrim — 3 zones stacked at bottom */}
      <div className={styles.cardBody}>
        {/* Zone 1: Peek — phase badge + title */}
        <div className={styles.cardPeek}>
          <div className={styles.phaseBadge}>
            <span className={styles.phaseDot} style={{ background: phaseColor }} />
            <span className={styles.phaseText}>{phaseLabel(ws.phase, t)}</span>
          </div>
          <h4 className={styles.cardTitle}>{ws.title || ws.presentingConcern}</h4>
        </div>

        {/* Zone 2: Scrollable description */}
        <div className={styles.cardScroll}>
          {ws.description && (
            <p className={styles.cardDesc}>{ws.description}</p>
          )}
        </div>

        {/* Zone 3: Sticky footer — segmented progress + meta */}
        <div className={styles.cardFooter}>
          <div className={styles.segmentedBar}>
            {WORKSPACE_PHASES.map((phase, idx) => {
              const isCurrent = idx === phaseIdx;
              const isCompleted = idx < phaseIdx;
              const segColor = isCompleted || isCurrent
                ? (PHASE_COLORS[phase] || "var(--text-muted)")
                : undefined;
              return (
                <div
                  key={phase}
                  className={
                    `${styles.segment}` +
                    `${isCompleted ? ` ${styles.segmentCompleted}` : ""}` +
                    `${isCurrent ? ` ${styles.segmentCurrent}` : ""}`
                  }
                  title={phaseLabel(phase, t)}
                >
                  <div
                    className={styles.segmentFill}
                    style={
                      isCompleted
                        ? { width: "100%", background: segColor }
                        : isCurrent
                          ? { width: "50%", background: segColor }
                          : undefined
                    }
                  />
                </div>
              );
            })}
          </div>
          <div className={styles.cardMeta}>
            <span>{ws.teamMemberCount} {t("workspaces.members")}</span>
            <span className={styles.metaSep}>·</span>
            <span>{ws.evidenceCount} {t("workspaces.evidence")}</span>
            <span className={styles.metaSep}>·</span>
            <span>{timeAgo(ws.updatedAt)}</span>
          </div>
        </div>
      </div>
    </button>
  );
};

export default WorkspaceCard;
